import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { Subscription, fromEvent, map, merge, of } from 'rxjs';
import { RouterOutlet } from '@angular/router';
import { LoadingBarModule } from '@ngx-loading-bar/core';


// Store
import { AppRepository } from './app.repository';
import { NavigationEnd, Router } from '@angular/router';
import { PropertyRepository } from './pages/shell/admin/property/property.repository';
import { OrganizationRepository } from './pages/shell/admin/organizations/organization.repository';
import { PropertyService } from './pages/shell/admin/property/property.service';
import { BuildingService } from './pages/shell/admin/building/building.service';
import { ThemeService } from './core/services/theme.service';

// Loading
import { AppLoadingService } from './core/services/app-loading.service';
import { AppSkeletonWithProgressComponent } from './shared/components/app-skeleton/app-skeleton-with-progress.component';

// Spartan UI
import { HlmToaster } from '@spartan-ng/helm/sonner';

// Language
import { TranslocoModule } from '@jsverse/transloco';
import { environment } from '@src/environments/environment';
import { PdfExportDownloadsPanelComponent } from './shared/components/pdf-export-downloads-panel/pdf-export-downloads-panel.component';

// PWA
import { PwaService } from './core/services/pwa.service';
import { PwaInstallBannerComponent } from './shared/components/pwa-install-banner/pwa-install-banner.component';

/**
 * The root component of the application.
 */
@Component({
  selector: 'app-root',
  styleUrls: ['./app.component.less'],
  templateUrl: './app.component.html',
  standalone: true,
  imports: [
    RouterOutlet,
    LoadingBarModule,
    HlmToaster,
    AppSkeletonWithProgressComponent,
    TranslocoModule,
    PdfExportDownloadsPanelComponent,
    PwaInstallBannerComponent,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  /**
   * Subscription for network status changes.
   */
  networkStatus$: Subscription = Subscription.EMPTY;

  /**
   * The application store.
   */
  readonly appRepository = inject(AppRepository);
  readonly organizationRepository = inject(OrganizationRepository);
  readonly themeService = inject(ThemeService);
  readonly loadingService = inject(AppLoadingService);
  readonly pwaService = inject(PwaService);
  public readonly isProduction = environment.production;

  constructor(
    private _router: Router,
    private _propertyService: PropertyService,
    private _buildingService: BuildingService,
  ) {
    // Initialize theme service - this ensures theme is applied on app startup
    this.themeService.getCurrentTheme();

    if (environment.pwaEnabled) {
      this.pwaService.initialize();
    }

    // Start the app initialization process
    this.initializeApplication();
  }

  /**
   * Initialize the application with loading states
   */
  private async initializeApplication(): Promise<void> {
    try {
      // Start loading operations
      this.loadingService.startOperation('theme', 'Loading theme settings...');
      this.loadingService.startOperation('config', 'Loading configuration...');

      // Initialize theme (already done in constructor, so complete immediately)
      await new Promise((resolve) => setTimeout(resolve, 300));
      this.loadingService.completeOperation('theme');

      // Simulate configuration loading
      await new Promise((resolve) => setTimeout(resolve, 500));
      this.loadingService.completeOperation('config');

      // Wait for authentication and user data
      this.loadingService.startOperation('auth', 'Authenticating user...');
      await new Promise((resolve) => setTimeout(resolve, 700));
      this.loadingService.completeOperation('auth');

      // Initialize services
      this.loadingService.startOperation('services', 'Initializing services...');
      await new Promise((resolve) => setTimeout(resolve, 400));
      this.loadingService.completeOperation('services');

      // Complete initialization
      this.loadingService.setLoadingMessage('Application ready');
      await new Promise((resolve) => setTimeout(resolve, 200));
      this.loadingService.setLoading(false);
    } catch (error) {
      console.error('Application initialization failed:', error);
      this.loadingService.setLoadingMessage('Failed to load application');
      // Force complete after error
      setTimeout(() => {
        this.loadingService.forceComplete();
      }, 2000);
    }
  }

  /**
   * Lifecycle hook that is called after the component is initialized.
   * It checks the network status.
   */
  ngOnInit(): void {
    this.checkNetworkStatus();

    /* this.user$.subscribe((user) => {
      if (user) {
        console.log(user);
        (<any>window).Intercom("boot", {
          api_base: "https://api-iam.intercom.io",
          app_id: "qkh88dfd",
          name: user.name, // Full name
          email: user.email, // the email for your user
          updated_at: user.updated_at, // Last update of the user
        });
      }
    }); */

    this.organizationRepository.currentOrganization$.subscribe((organization) => {
      if (organization) {
        // Track these operations in loading service
        this.loadingService
          .trackObservable(
            this._buildingService.getAllByOrganizationId(organization._id),
            'buildings',
            'Loading buildings...',
          )
          .subscribe();

        this.loadingService
          .trackObservable(
            this._propertyService.getAllByOrganizationId(organization._id),
            'properties',
            'Loading properties...',
          )
          .subscribe();
      }
    });
  }

  /**
   * Checks the network status and updates the application store banner accordingly.
   */
  checkNetworkStatus() {
    this.networkStatus$ = merge(of(null), fromEvent(window, 'online'), fromEvent(window, 'offline'))
      .pipe(map(() => navigator.onLine))
      .subscribe((status) => {
        if (status) {
          this.appRepository.clearBanner();
        } else {
          this.appRepository.setBanner({ message: 'No internet connection' });
        }
      });
  }

  /**
   * Lifecycle hook that is called when the component is about to be destroyed.
   * It unsubscribes from the network status subscription.
   */
  ngOnDestroy(): void {
    this.networkStatus$.unsubscribe();
    this.pwaService.destroy();
  }
}
